import { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, Newspaper, Sparkles, Eraser, Loader2, CheckCircle2, XCircle, AtSign, Rocket, Gavel, Gamepad2, Clapperboard, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtAgo, fmtUntil } from "@/lib/catalysts";

const fmtTs = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" }) : "—");
const errs = (o) => (o && Object.keys(o).length ? Object.entries(o).map(([k, v]) => `${k}: ${v}`).join("; ") : "");

function Card({ title, last, next, testid, children, span }) {
  return (
    <div className={`rounded-lg border border-[#222F43] bg-[#111723] p-3 ${span ? "sm:col-span-2" : ""}`} data-testid={testid}>
      <div className="font-mono uppercase tracking-widest text-[10px] text-slate-500 mb-1">{title}</div>
      <div className="text-slate-300">Last: {last || "never"}</div>
      {next && <div className="text-slate-400">Next: {fmtTs(next)} <span className="text-slate-600">({fmtUntil(next)})</span></div>}
      {children}
    </div>
  );
}

function TrackingEditor({ initial, onSaved }) {
  const [v, setV] = useState({ games: "", movies: "", courtParties: "", newsQueries: "", earningsTickers: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (initial) setV(Object.fromEntries(Object.entries(initial).map(([k, a]) => [k, (a || []).join(", ")]))); }, [initial]);
  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(Object.entries(v).map(([k, s]) => [k, s.split(",").map((x) => x.trim()).filter(Boolean)]));
      await api.put("/catalyst-tracking", body);
      toast.success("Tracking lists saved — used on the next sync");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };
  // render helper (not a component) so inputs keep focus across re-renders
  const field = ({ k, label, ph }) => (
    <label className="block text-xs">
      <span className="text-slate-400">{label}</span>
      <Input value={v[k]} onChange={(e) => setV((s) => ({ ...s, [k]: e.target.value }))} placeholder={ph} className="mt-1 h-8 bg-[#0B1018] border-[#334566] text-xs" data-testid={`tracking-${k}`} />
    </label>
  );
  return (
    <div className="rounded-lg border border-[#222F43] bg-[#111723] p-3 space-y-2" data-testid="tracking-editor">
      <div className="font-mono uppercase tracking-widest text-[10px] text-slate-500">Tracked names (comma-separated)</div>
      <div className="grid sm:grid-cols-2 gap-2">
        {field({ k: "games", label: "Games to always include (IGDB)", ph: "Hollow Knight: Silksong, GTA VI" })}
        {field({ k: "courtParties", label: "Court parties to watch (adds Google News queries)", ph: "Do Kwon, Tornado Cash" })}
        {field({ k: "newsQueries", label: "Extra Google News queries", ph: "Pump.fun, Hyperliquid" })}
        {field({ k: "movies", label: "Movies to always include", ph: "Dune Part Three" })}
        <div className="sm:col-span-2">{field({ k: "earningsTickers", label: "Tracked earnings tickers (Finnhub) — synced daily, auto-published", ph: "COIN, MSTR, HOOD, TSLA, NVDA" })}</div>
      </div>
      <Button size="sm" onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-400 text-black h-8" data-testid="tracking-save-btn">
        {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />} Save
      </Button>
    </div>
  );
}

export default function SourcesPanel({ open, onOpenChange, onChanged }) {
  const [h, setH] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => { try { const { data } = await api.get("/catalysts/health"); setH(data); } catch { /* ignore */ } }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const run = async (key, path, label) => {
    setBusy(key);
    try {
      const { data } = await api.post(path, key === "generate" ? { months: 2 } : undefined);
      if (data.skipped) toast(`${label}: ${data.reason}`);
      else if (data.queued) {
        toast(`${label} started in the background`, { description: "Takes 1–3 min (rate-limited APIs). Health below refreshes automatically." });
        [20, 60, 120, 180].forEach((s) => setTimeout(() => { load(); onChanged?.(); }, s * 1000));
      }
      else if (["sync", "spacex", "daily", "weekly"].includes(key)) toast.success(`${label}: ${data.fetched} events · ${data.created} new · ${data.updated} updated`, errs(data.errors) ? { description: errs(data.errors) } : undefined);
      else if (key === "news") toast.success(`News scan: ${data.itemsScanned} articles → ${data.created} new for review · ${data.updates.length} updates · ${(data.viral || []).length} viral`, data.error ? { description: data.error } : undefined);
      else if (key === "xwatch" || key === "studio") toast.success(`${label}: ${data.postsScanned} new posts → ${data.created} new for review · ${data.merged} merged`, data.error ? { description: data.error } : undefined);
      else if (key === "poly") toast.success(`Polymarket: ${data.marketsScanned} markets → ${data.created} new for review · ${data.merged} merged`, data.error ? { description: data.error } : undefined);
      else if (key === "cleanup") toast.success(`Cleanup: ${data.deleted.length} deleted · ${data.merged.length} merged`);
      else toast.success(`AI drafted ${data.created} events into the review queue`);
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || `${label} failed`); }
    finally { setBusy(""); }
  };

  const Btn = ({ k, path, icon: Icon, label, cls }) => (
    <Button size="sm" variant="outline" disabled={Boolean(busy)} onClick={() => run(k, path, label)} className={`bg-transparent ${cls}`} data-testid={`sources-${k}-btn`}>
      {busy === k ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Icon className="h-3.5 w-3.5 mr-1.5" />} {label}
    </Button>
  );
  const g = h?.groups || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-[#334566] max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="sources-panel">
        <DialogHeader>
          <DialogTitle className="font-display">Sources & automation</DialogTitle>
          <DialogDescription className="text-slate-400">Everything runs on a schedule and writes to our database — the calendar never calls these APIs directly. Run anything now.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          <Btn k="full" path="/catalysts/full-refresh" icon={RefreshCw} label="Refresh everything" cls="border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/10 font-semibold" />
          <Btn k="sync" path="/catalysts/sync" icon={RefreshCw} label="Sync official" cls="border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10" />
          <Btn k="spacex" path="/catalysts/sources/12h" icon={Rocket} label="SpaceX" cls="border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10" />
          <Btn k="daily" path="/catalysts/sources/daily" icon={Gavel} label="Supreme Court" cls="border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10" />
          <Btn k="weekly" path="/catalysts/sources/weekly" icon={Gamepad2} label="Games & films" cls="border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10" />
          <Btn k="news" path="/catalysts/news-scan" icon={Newspaper} label="Scan news + PR + courts" cls="border-blue-500/50 text-blue-300 hover:bg-blue-500/10" />
          <Btn k="poly" path="/catalysts/polymarket" icon={Sparkles} label="Polymarket" cls="border-indigo-500/50 text-indigo-300 hover:bg-indigo-500/10" />
          <Btn k="xwatch" path="/catalysts/x-watch" icon={AtSign} label="Check X" cls="border-sky-500/50 text-sky-300 hover:bg-sky-500/10" />
          <Btn k="studio" path="/catalysts/x-studio-watch" icon={Clapperboard} label="Studio X" cls="border-sky-500/50 text-sky-300 hover:bg-sky-500/10" />
          <Btn k="generate" path="/catalysts/generate" icon={Sparkles} label="AI draft" cls="border-purple-500/50 text-purple-300 hover:bg-purple-500/10" />
          <Btn k="cleanup" path="/catalysts/cleanup" icon={Eraser} label="Cleanup & dedupe" cls="border-amber-500/50 text-amber-300 hover:bg-amber-500/10" />
        </div>
        {h && (
          <div className="grid sm:grid-cols-2 gap-3 mt-2 text-xs">
            <Card title="Everything · weekly Sun 06:00 UTC · all sources in one pass (also runs once on a fresh install)" testid="health-full" next={h.nextRuns?.fullRefresh} span
              last={h.fullRefresh?.running ? `running since ${fmtAgo(h.fullRefresh.running.at)} (${h.fullRefresh.running.trigger})` : h.fullRefresh?.last?.at ? `${fmtAgo(h.fullRefresh.last.at)} · ${h.fullRefresh.last.trigger}` : ""}>
              {h.fullRefresh?.last?.steps && (
                <div className="text-slate-500 flex flex-wrap gap-x-3">
                  {Object.entries(h.fullRefresh.last.steps).map(([k, s]) => (
                    <span key={k} className={s.error ? "text-red-400" : ""}>{k}: {s.error ? s.error.slice(0, 60) : `${s.fetched ?? s.marketsScanned ?? s.itemsScanned ?? s.postsScanned ?? 0} in · ${s.created ?? 0} new`}</span>
                  ))}
                </div>
              )}
            </Card>
            <Card title="Official · weekly Mon 06:00 UTC · Fed, BLS, unlocks" testid="health-official" next={h.nextRuns?.officialSync}
              last={h.officialSync?.at ? `${fmtAgo(h.officialSync.at)} · ${h.officialSync.fetched} events` : ""}>
              {errs(h.officialSync?.errors) && <div className="text-red-400 mt-1">Errors: {errs(h.officialSync.errors)}</div>}
            </Card>
            <Card title="SpaceX · every 12h · Launch Library 2" testid="health-spacex" next={h.nextRuns?.spacex}
              last={g["12h"]?.at ? `${fmtAgo(g["12h"].at)} · ${g["12h"].fetched} launches` : ""}>
              {errs(g["12h"]?.errors) && <div className="text-red-400 mt-1">Errors: {errs(g["12h"].errors)}</div>}
            </Card>
            <Card title="Supreme Court + Polymarket · daily 05:00 UTC" testid="health-daily" next={h.nextRuns?.daily}
              last={g.daily?.at ? `${fmtAgo(g.daily.at)} · ${g.daily.fetched} SCOTUS cases` : ""}>
              <div className="text-slate-400">Polymarket: {h.polymarket?.at ? `${fmtAgo(h.polymarket.at)} · ${h.polymarket.marketsScanned} markets → ${h.polymarket.created} queued` : "never"}</div>
              {(errs(g.daily?.errors) || h.polymarket?.error) && <div className="text-red-400 mt-1">{errs(g.daily?.errors)} {h.polymarket?.error}</div>}
            </Card>
            <Card title="Games & films · weekly Tue 07:00 UTC · IGDB, Wikipedia" testid="health-weekly" next={h.nextRuns?.weekly}
              last={g.weekly?.at ? `${fmtAgo(g.weekly.at)} · ${g.weekly.fetched} releases` : ""}>
              {errs(g.weekly?.errors) && <div className="text-red-400 mt-1">Errors: {errs(g.weekly.errors)}</div>}
            </Card>
            <Card title="News + PR + courts · every 12h" testid="health-news" next={h.nextRuns?.newsScan}
              last={h.newsScan?.at ? `${fmtAgo(h.newsScan.at)} · ${h.newsScan.itemsScanned} items → ${h.newsScan.created} queued, ${h.newsScan.updates?.length || 0} updates` : ""}>
              {h.newsScan?.byKind && <div className="text-slate-500">news {h.newsScan.byKind.news} · PR {h.newsScan.byKind.pr} · court {h.newsScan.byKind.court}</div>}
              {h.newsScan?.error && <div className="text-red-400 mt-1">{h.newsScan.error}</div>}
              <div className="text-slate-400 mt-1">Pending review: <span className="text-amber-300 font-mono" data-testid="health-pending">{h.pendingCount}</span> · Viral upcoming: <span className="text-orange-300 font-mono" data-testid="health-viral">{h.viralCount}</span></div>
            </Card>
            <Card title={`X watch · every 2h · ${(h.xAccounts || []).map((a) => `@${a}`).join(", ")}`} testid="health-x" next={h.nextRuns?.xWatch}
              last={h.xWatch?.at ? `${fmtAgo(h.xWatch.at)} · ${h.xWatch.postsScanned} new posts → ${h.xWatch.created} queued` : ""}>
              {(h.xWatch?.error || errs(h.xWatch?.errors)) && <div className="text-red-400 mt-1">{h.xWatch?.error} {errs(h.xWatch?.errors)}</div>}
            </Card>
            <Card title={`Studio X · every 6h · ${(h.xStudioAccounts || []).map((a) => `@${a}`).join(", ")}`} testid="health-studio" next={h.nextRuns?.xStudioWatch} span
              last={h.xStudioWatch?.at ? `${fmtAgo(h.xStudioWatch.at)} · ${h.xStudioWatch.postsScanned} new posts → ${h.xStudioWatch.created} queued` : ""}>
              {(h.xStudioWatch?.error || errs(h.xStudioWatch?.errors)) && <div className="text-red-400 mt-1">{h.xStudioWatch?.error} {errs(h.xStudioWatch?.errors)}</div>}
            </Card>
          </div>
        )}
        {h && <TrackingEditor initial={h.tracking} onSaved={load} />}
        {h && (
          <div className="mt-2" data-testid="health-feeds">
            <div className="font-mono uppercase tracking-widest text-[10px] text-slate-500 mb-1.5">Feeds ({h.feeds.length}) — status from last scan</div>
            <ul className="grid sm:grid-cols-2 gap-1">
              {h.feeds.map((f) => (
                <li key={f.url} className="flex items-center gap-2 text-xs" data-testid={`feed-${f.name.replace(/\W+/g, "-").toLowerCase()}`}>
                  {f.error ? <XCircle className="h-3.5 w-3.5 text-red-400 shrink-0" /> : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" />}
                  <span className="text-slate-300 truncate">{f.name}</span>
                  <span className="text-[9px] font-mono uppercase text-slate-600">{f.kind}</span>
                  {f.error && <span className="text-red-400 truncate" title={f.error}>— {f.error}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
